import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {OmgRawMessage} from "./omg.service.interface";
import { observable, runInAction, makeObservable } from "mobx";
import {Check} from "@sparkware/app-utils";
import {LocalStorageKeys} from "../storage/local-storage-keys";

export class OmgMessageModel {
    constructor(public readonly rawMessage: OmgRawMessage) {
    }

    public get id(): string {
        return this.rawMessage["@id"];
    }

    public get displayInGame(): boolean {
        const inGame = this.rawMessage["@displayInGame"]?.toString()?.toLowerCase();
        return inGame === 'true' || inGame === '1';
    }

    public get messageType(): string {
        return this.rawMessage["@messageType"];
    }

    public get templateId(): string {
        return this.rawMessage["@templateId"];
    }

    toXml(): string {
        return `<Message id="${this.id}" templateId="${this.templateId}" messageType="${this.messageType}" displayInGame="${this.displayInGame ? 'True' : 'False'}"></Message>`
    }
}

export class OmgMessageCollection {
    constructor(private readonly services: IServiceFactoryExtended) {
        makeObservable(this);

        this._displayedMessages = this._getPersistedMessages();
    }

    @observable
    private _allMessages: OmgMessageModel[] = [];

    @observable.ref
    private _displayedMessages: OmgRawMessage[] = [];

    push(messages: OmgRawMessage | OmgRawMessage[]): void {

        if(!messages) {
            return;
        }

        let messagesAsArray: OmgRawMessage[] = [];

        if(Check.isArray(messages)) {
            messagesAsArray = messages;
        } else {
            messagesAsArray.push(messages); //this is because if there is only one message the server doesn't return an array but instead an object.
        }
        runInAction(() => {
            for(let m of messagesAsArray) {
                const existingMessageIndex = this._allMessages.findIndex(existingMessage => existingMessage.id === m["@id"]);
                if(existingMessageIndex >= 0) {
                    this._allMessages[existingMessageIndex] = new OmgMessageModel(m);
                } else {
                    this._allMessages.push(new OmgMessageModel(m));
                }
            }
        });
    }

    clear(): void {
        runInAction(() => {
            this._allMessages = [];
            this._displayedMessages = [];
        });
        this._removePersistedMessages();
    }


    public markMessagesAsDisplayed(displayedMessages: OmgMessageModel[]): void {
        displayedMessages = displayedMessages ?? [];
        const messagesToPersist: Record<string, OmgRawMessage> = this._getPersistedMessages().toDictionary(msg => msg["@id"]);
        for(let newMsg of displayedMessages) {
            messagesToPersist[newMsg.id] = newMsg.rawMessage;
        }

        if(Check.isEmpty(messagesToPersist)) {
            this._removePersistedMessages();
        } else {
            this.services.localStorage.setJson(LocalStorageKeys.uccMessages, Object.values(messagesToPersist));
        }

        runInAction(() => {
            this._displayedMessages = Object.values(messagesToPersist);
        });
    }

    private _removePersistedMessages(): void {
        this.services.localStorage.removeItem(LocalStorageKeys.uccMessages);
    }

    private _getPersistedMessages(): OmgRawMessage[] {
        return this.services.localStorage.getJson<OmgRawMessage[]>(LocalStorageKeys.uccMessages) || [];
    }

    public getMessagesToDisplay(): OmgMessageModel[] {
        const alreadyDisplayedMessages = this._displayedMessages.toDictionary(msg => msg["@id"]);
        const newMessagesToDisplay: OmgMessageModel[] = [];

        for(let msg of this._allMessages) {
            if(!alreadyDisplayedMessages[msg.id]) {
                newMessagesToDisplay.push(msg);
            }
        }

        if(newMessagesToDisplay.length > 0) {
            return newMessagesToDisplay.slice(0, Math.min(3, newMessagesToDisplay.length));
        }

        return this._allMessages.slice(0, Math.min(3, this._allMessages.length));
    }

}